// Types for ingredient plant-to-plant transfers ("traslados", P1 <-> P2).
// One-step, multi-item transfer of oils + raw materials between plants, with a
// documentary record (header + line items). Backed by the create_ingredient_transfer
// RPC and the ingredient_transfers / ingredient_transfer_items tables.

import { z } from "zod";

import type { IngredientType } from "~/types/purchasing";
import type { Database } from "~/types/supabase";

// Derive DB row shapes from the generated types (single source of truth).
export type IngredientTransferRow =
  Database["public"]["Tables"]["ingredient_transfers"]["Row"];
export type IngredientTransferItemRow =
  Database["public"]["Tables"]["ingredient_transfer_items"]["Row"];

// Read model: a line item with the joined oil/raw-material names.
export interface IngredientTransferItemWithNames extends IngredientTransferItemRow {
  oil_type?: {
    id: string;
    name: string;
    code: string | null;
    // liters * density = kg. Oils are stored in liters but shown/entered in kg.
    density_kg_per_liter: number;
  } | null;
  raw_material?: {
    id: string;
    name: string;
    code: string | null;
    unit: string;
  } | null;
}

// Read model: a transfer header with its lines and the performer's name.
export interface IngredientTransferWithItems extends IngredientTransferRow {
  items: IngredientTransferItemWithNames[];
  performed_by_profile?: {
    id: string;
    name: string | null;
    lastname: string | null;
  } | null;
}

// Return value of the create_ingredient_transfer RPC.
export interface CreateTransferResult {
  id: string;
  transfer_number: string;
}

// Payload for a single line sent to the RPC.
export interface TransferItemInput {
  ingredient_type: IngredientType;
  oil_type_id: string | null;
  raw_material_id: string | null;
  quantity: number;
  unit: string;
}

// -----------------------------------------------------------------------------
// Form schema (react-hook-form + zodResolver)
// -----------------------------------------------------------------------------

export const transferItemSchema = z.object({
  ingredient_type: z.enum(["oil", "raw_material"]),
  oil_type_id: z.string().uuid().nullable(),
  raw_material_id: z.string().uuid().nullable(),
  // display-only label carried by the field array (not sent to the backend)
  name: z.string(),
  quantity: z.coerce.number().positive("La cantidad debe ser mayor a 0"),
  unit: z.string().min(1),
});

export const createTransferSchema = z
  .object({
    from_plant: z.enum(["P1", "P2", "NC"]),
    to_plant: z.enum(["P1", "P2", "NC"]),
    notes: z.string().optional(),
    items: z.array(transferItemSchema).min(1, "Agrega al menos un ingrediente"),
  })
  .refine((v) => v.from_plant !== v.to_plant, {
    message: "La planta de origen y destino no pueden ser iguales",
    path: ["to_plant"],
  });

export type TransferItemFormData = z.infer<typeof transferItemSchema>;
export type CreateTransferFormData = z.infer<typeof createTransferSchema>;
